import { column, Css, GridColumn, GridDataRow, GridTable, simpleHeader, Tooltip } from "@homebound/beam";
import { format } from "date-fns";
import { useMemo } from "react";
import { emptyCellDash, Icon, linkHeader, percentageCell, priceCell } from "src/components";
import { createProjectUrl } from "src/RouteUrls";
import type { DirectMarginTab_ProjectFragment } from "./DirectMarginTab.generated";

type DirectMarginPerProjectTableProps = {
  projects: DirectMarginTab_ProjectFragment[];
  search?: string;
};

export function DirectMarginPerProjectTable({ projects, search }: DirectMarginPerProjectTableProps) {
  const columns = useMemo(() => createColumns(), []);
  const rows = useMemo(() => createRows(projects), [projects]);
  return (
    <div css={Css.mt2.$}>
      <GridTable columns={columns} rows={rows} filter={search} />
    </div>
  );
}

type HeaderRow = { kind: "header" };
type ProjectRow = { kind: "project"; id: string; data: DirectMarginTab_ProjectFragment };
type Row = HeaderRow | ProjectRow;

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Project",
      project: ({ id, name }) => linkHeader(name, createProjectUrl(id)),
    }),
    column<Row>({
      header: "Start Date",
      project: ({ readyToStartDate }) => (readyToStartDate ? format(readyToStartDate, "MMM d, yyyy") : emptyCellDash),
    }),
    column<Row>({
      header: () => (
        <div css={Css.df.cg1.$}>
          Sales Price ($)
          <Tooltip title="For homes that have not sold yet, these are estimates." placement="top">
            <Icon icon="infoCircle" inc={1.9} />
          </Tooltip>
        </div>
      ),
      project: ({ grossSalesPriceInCents, snapshotAsOf }) =>
        priceCell({ valueInCents: grossSalesPriceInCents, comparedValueInCents: snapshotAsOf?.grossSalesPriceInCents }),
    }),
    column<Row>({
      header: "Soft Costs ($)",
      project: ({ totalSoftCostInCents, snapshotAsOf }) =>
        priceCell({
          valueInCents: totalSoftCostInCents,
          comparedValueInCents: snapshotAsOf?.totalSoftCostInCents,
          invertComparedValueColors: true,
        }),
    }),
    column<Row>({
      header: "Hard Costs ($)",
      project: ({ totalHardCostInCents, snapshotAsOf }) =>
        priceCell({
          valueInCents: totalHardCostInCents,
          comparedValueInCents: snapshotAsOf?.totalHardCostInCents,
          invertComparedValueColors: true,
        }),
    }),
    column<Row>({
      header: "Land Acq. Cost ($)",
      project: ({ purchaseCostInCents }) => priceCell({ valueInCents: purchaseCostInCents }),
    }),
    column<Row>({
      header: "Direct Margin ($)",
      project: ({ closingCostsForDirectMarginInCents, snapshotAsOf }) =>
        priceCell({
          valueInCents: closingCostsForDirectMarginInCents,
          comparedValueInCents: snapshotAsOf?.closingCostsForDirectMarginInCents,
        }),
    }),
    column<Row>({
      header: "Direct Margin (%)",
      project: ({ id, directMarginInBasisPoints, snapshotAsOf }) =>
        percentageCell(directMarginInBasisPoints ? directMarginInBasisPoints / 100 : undefined, {
          id,
          comparedValuePercent: snapshotAsOf?.directMarginInBasisPoints
            ? snapshotAsOf.directMarginInBasisPoints / 100
            : undefined,
        }),
    }),
  ];
}

function createRows(projects: DirectMarginTab_ProjectFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...projects.map((project) => ({ kind: "project" as const, id: project.id, data: project }))];
}
